import { useServerInfo } from '@go-client/context/ServerInfoContext';
import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { BrandMapping } from '../../server/types/brandMapping';
import { getBrandMappings } from '@go-client/lib/api/brandMapping';
import PageWrapper from '@go-client/pages/PageWrapper';
import Page from '@go-client/pages/Page';
import { MenuBar } from '@go-client/components';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';
import PropertiesNavigation from '@go-client/components/Navigations/PropertiesNavigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BrandMappingsForm from '@go-client/components/BasicForms/BrandMappingsForm';
import './SuperAdminConfig.scss';

export type SuperAdminConfigPageProps = RouteComponentProps<{}>;

type SuperAdminCategory = 'maintenance' | 'brandMappings';

interface SuperAdminConfigCategory {
  key: SuperAdminCategory;
  name: string;
}

export default function SuperAdminConfig(props: SuperAdminConfigPageProps) {
  const serverInfo = useServerInfo();

  const { brand, refreshContext: refreshActiveBrand } = serverInfo;
  const actionsRef = useRef<HTMLDivElement>(null);
  const [actionsElem, setActionsElem] = useState<HTMLDivElement | null>(null);
  const [brandMappings, setBrandMappings] = useState<BrandMapping[]>([]);

  useEffect(() => {
    setActionsElem(actionsRef.current);
  }, []);

  useEffect(() => {
    getBrandMappings(brand._id)
      .then(mappings => setBrandMappings(mappings))
      .catch(_error => console.error(`Error loading brand mappings`));
  }, [brand]);

  const categories: SuperAdminConfigCategory[] = [];

  categories.push({
    key: 'brandMappings',
    name: 'Brand Mappings',
  });

  const [activeCategory, setActiveCategory] = useState<SuperAdminConfigCategory | undefined>(
    categories[0]
  );

  return (
    <PageWrapper {...props}>
      <Page className="SuperAdminConfig absolute flex items-stretch">
        <MenuBar {...props} />
        <div className="SuperAdminConfigContent bg-blue-lightest overflow-y-auto flex-grow">
          <div
            className="SuperAdminConfigHeader align-with-logo flex items-end sticky top-0
            left-0 z-10 bg-white shadow"
          >
            <BrandLogo logo={brand.logo} />
            <div className="mb-3 px-5 pl-8 flex-grow flex items-end">
              <PropertiesNavigation active="super-admin-config" />
            </div>
          </div>
          <div className="SuperAdminConfigBody content-minus-logo">
            <div style={{ maxWidth: '1750px' }} className="mx-auto">
              <div
                className="sticky w-full left-0 bg-blue-lightest"
                style={{ top: '130px', height: '2rem', zIndex: 2 }}
              />
              <div className="px-10">
                <div className="grid grid-cols-4 gap-4 items-start">
                  <div className="bg-white shadow-md rounded overflow-clip">
                    {categories.map(c => (
                      <div
                        key={c.key}
                        className={`list-item px-5 py-3 cursor-pointer ${
                          activeCategory && activeCategory.key === c.key ? 'active' : ''
                        }`}
                        onClick={() => setActiveCategory(c)}
                      >
                        <div className="flex items-center">
                          <div className="flex-grow min-w-0">
                            <div className="list-item-name font-semibold overflow-ellipsis">
                              {c.name}
                            </div>
                          </div>
                          <FontAwesomeIcon
                            icon="chevron-right"
                            className="list-item-arrow ml-auto pl-3 flex-shrink-0"
                          />
                        </div>
                      </div>
                    ))}
                    {categories.length < 1 && (
                      <div className="grid items-center justify-center italic text-smoke bg-eggshell px-3 py-5">
                        No configurable options found
                      </div>
                    )}
                  </div>
                  {activeCategory && (
                    <div
                      className="sticky col-span-3"
                      style={{
                        top: 'calc(130px + 2rem)',
                        height: 'calc(100vh - 130px - 4rem)',
                      }}
                    >
                      <div className="bg-white shadow-md max-h-full flex flex-col rounded overflow-clip">
                        <div className="flex-grow overflow-auto">
                          {activeCategory.key === 'brandMappings' && actionsElem && (
                            <div className="p-5 grid grid-cols-1 gap-4">
                              <BrandMappingsForm
                                edit={brandMappings}
                                actionsElem={actionsElem}
                                onFormSubmitted={refreshActiveBrand}
                              />
                            </div>
                          )}
                          <div
                            className="sticky bottom-0 p-3 bg-eggshell border-t"
                            style={{ zIndex: 1001 }}
                            ref={actionsRef}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-3">&nbsp;</div>
              </div>
            </div>
          </div>
        </div>
      </Page>
    </PageWrapper>
  );
}
